import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonIcon,
  IonSpinner,
  IonText,
  IonAlert,
  IonToggle,
} from '@ionic/react';
import { createOutline, refreshOutline, eyeOutline, checkmarkCircle, ellipseOutline, notificationsOutline, chevronForwardOutline } from 'ionicons/icons';
import { useLotDetail } from '../../application/use-cases/lots/useLotDetail';
import { LOT_ACCESS_LABEL, formatDate, formatGdd, isPasture, stageTitle } from '../../domain/Lot';
import DeleteRow from '../components/DeleteRow';
import LotShareCard from '../components/lot/LotShareCard';
import { round1, shortDate } from '../../domain/WeatherStation';
import ProgressRing from '../components/lot/ProgressRing';
import './Lots.css';

// admin: abierto desde el panel de admin (ve y edita como el dueño; no
// borra, no comparte y no tiene aviso propio).
const LotDetail: React.FC<{ admin?: boolean }> = ({ admin = false }) => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { lot, loading, error, busy, actionError, observeStage, restartCycle, setNotifyStageChange, remove } = useLotDetail(id, admin);
  const base = admin ? '/app/admin/lots' : '/app/lots';
  const isOwner = admin || lot?.access === 'owner';
  const canEdit = admin || (lot !== null && lot.access !== 'view');
  const [observing, setObserving] = useState<number | null>(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const status = lot?.status ?? null;
  const ph = status?.phenology;
  const maxStationMm = status?.rain ? Math.max(1, ...status.rain.perStation.map((s) => s.mm)) : 1;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={base} />
          </IonButtons>
          <IonTitle>{lot?.name ?? 'Lote'}</IonTitle>
          {lot && canEdit && (
            <IonButtons slot="end">
              <IonButton onClick={() => history.push(`${base}/${id}/edit`)} aria-label="Editar lote">
                <IonIcon icon={createOutline} />
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {!loading && error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {lot && (
          <div className="lot-detail">
            <div className="lot-card-cover lot-detail-cover">
              <span className="lot-card-cover-text">
                <strong>{lot.name}</strong>
                <small>
                  {[lot.crop, lot.areaHa !== null ? `${lot.areaHa.toLocaleString('es-AR')} ha` : null].filter(Boolean).join(' · ')}
                </small>
              </span>
              {status && ph && <ProgressRing pct={ph.progressPct} />}
            </div>
            <IonCard>
              <IonCardContent>
                {admin && <p className="lot-detail-note">Viendo como admin el lote de {lot.ownerName}.</p>}
                {!admin && lot.access !== 'owner' && (
                  <span className="lot-shared-badge">
                    Compartido por {lot.ownerName} · {LOT_ACCESS_LABEL[lot.access]}
                  </span>
                )}
                <p className="lot-detail-meta">
                  {[lot.crop, lot.areaHa !== null ? `${lot.areaHa.toLocaleString('es-AR')} ha` : null, `base ${round1(lot.baseTemp)} °C`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>

                {!lot.cycleStart && (
                  <p className="lot-card-note">Cargá la fecha de siembra para empezar a sumar grados día.</p>
                )}
                {lot.cycleStart && lot.stations.length === 0 && (
                  <p className="lot-card-note">Sumá al menos una estación con temperatura para seguir el cultivo.</p>
                )}

                {status && ph && (
                  <>
                    <p className="lot-detail-label">Etapa actual</p>
                    <p className="lot-detail-stage">{stageTitle(lot.stages[ph.stageIndex])}</p>
                    <span className="lot-progress big">
                      <span style={{ width: `${ph.progressPct}%` }} />
                    </span>
                    <div className="lot-detail-stats">
                      <div>
                        <span>Grados día</span>
                        <strong>{formatGdd(ph.accumulated)} °Cd</strong>
                      </div>
                      <div>
                        <span>Avance del ciclo</span>
                        <strong>{ph.progressPct}%</strong>
                      </div>
                      <div>
                        <span>{isPasture(lot.crop) ? 'Desde el corte' : 'Siembra'}</span>
                        <strong>{lot.cycleStart && formatDate(lot.cycleStart)}</strong>
                      </div>
                    </div>
                    {ph.nextIndex !== null ? (
                      <p className="lot-detail-next">
                        Próxima: <strong>{stageTitle(lot.stages[ph.nextIndex])}</strong> · faltan {formatGdd(ph.remaining ?? 0)} °Cd
                        {ph.nextDate ? ` · fecha estimada ${shortDate(ph.nextDate)}` : ''}
                      </p>
                    ) : (
                      <p className="lot-detail-next">Llegó a la última etapa del ciclo.</p>
                    )}
                    <p className="lot-detail-note">
                      {status.last7Avg !== null && `${round1(status.last7Avg)} °Cd por día la última semana. `}
                      {lot.gddOffset !== 0 &&
                        `Incluye una corrección de ${lot.gddOffset > 0 ? '+' : ''}${formatGdd(lot.gddOffset)} °Cd por lo observado en el campo. `}
                    </p>
                    {status.missingDays > 0 && (
                      <p className="lot-detail-note level-caution">
                        {status.missingDays === 1 ? '1 día' : `${status.missingDays} días`} sin datos completos de temperatura no
                        suman: el acumulado real es mayor. Podés corregirlo marcando la etapa que ves en el lote.
                      </p>
                    )}
                    {isPasture(lot.crop) && canEdit && (
                      <IonButton fill="outline" size="small" disabled={busy} onClick={() => setConfirmRestart(true)}>
                        <IonIcon icon={refreshOutline} slot="start" />
                        Corte o pastoreo hoy: reiniciar
                      </IonButton>
                    )}
                  </>
                )}
              </IonCardContent>
            </IonCard>

            {status && ph && (
              <IonCard>
                <IonCardContent>
                  <p className="lot-section-title">Qué vigilar</p>
                  <p className="lot-watch">
                    {lot.stages[ph.stageIndex].watch ?? 'Sin indicaciones cargadas para esta etapa.'}
                  </p>
                  <div className="lot-last7">
                    <div className={status.last7.coldest !== null && status.last7.coldest <= 0 ? 'is-bad' : ''}>
                      <span>Mínima</span>
                      <strong>{status.last7.coldest !== null ? `${round1(status.last7.coldest)}°` : '—'}</strong>
                    </div>
                    <div className={status.last7.hottest !== null && status.last7.hottest >= 30 ? 'is-caution' : ''}>
                      <span>Máxima</span>
                      <strong>{status.last7.hottest !== null ? `${round1(status.last7.hottest)}°` : '—'}</strong>
                    </div>
                    <div>
                      <span>Hoja mojada</span>
                      <strong>{status.last7.wetHours !== null ? `${status.last7.wetHours} h` : '—'}</strong>
                    </div>
                    <div>
                      <span>Lluvia</span>
                      <strong>{status.last7.rainMm !== null ? `${round1(status.last7.rainMm)} mm` : '—'}</strong>
                    </div>
                  </div>
                  <p className="lot-detail-note">Últimos 7 días, en la estación más fría, la más caliente y la más húmeda del lote.</p>
                </IonCardContent>
              </IonCard>
            )}

            <IonCard>
              <IonCardContent>
                <p className="lot-section-title">Etapas</p>
                <ol className="lot-stages">
                  {lot.stages.map((stage, i) => {
                    const done = ph ? i < ph.stageIndex : false;
                    const current = ph ? i === ph.stageIndex : false;
                    return (
                      <li key={i} className={current ? 'is-current' : done ? 'is-done' : ''}>
                        <IonIcon icon={done || current ? checkmarkCircle : ellipseOutline} />
                        <span className="lot-stage-name">{stageTitle(stage)}</span>
                        <span className="lot-stage-gdd">{formatGdd(stage.gdd)} °Cd</span>
                        {status && !isPasture(lot.crop) && canEdit && (
                          <button className="lot-stage-observe" disabled={busy} onClick={() => setObserving(i)}>
                            <IonIcon icon={eyeOutline} />
                            La veo hoy
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {status && !isPasture(lot.crop) && canEdit && (
                  <p className="lot-detail-note">
                    Si en el lote ves una etapa distinta a la calculada, marcala: el acumulado se corrige y las fechas se
                    recalculan.
                  </p>
                )}
                {actionError && (
                  <IonText color="danger">
                    <p style={{ fontSize: 13 }}>{actionError}</p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>

            {!admin && (
              <IonCard>
                <IonCardContent>
                  <p className="lot-section-title">Avisos</p>
                  <label className="lot-notify">
                    <span>
                      Avisarme cuando cambia de etapa
                      <small>Te llega una notificación con qué vigilar en la etapa nueva.</small>
                    </span>
                    <IonToggle
                      aria-label="Avisarme cuando cambia de etapa"
                      checked={lot.notifyStageChange}
                      disabled={busy}
                      onIonChange={(e) => setNotifyStageChange(e.detail.checked)}
                    />
                  </label>
                  {lot.stations.length > 0 && (
                    <button className="lot-notify-link" onClick={() => history.push('/app/alerts')}>
                      <IonIcon icon={notificationsOutline} />
                      <span>
                        Alertas por etapa
                        <small>Helada en floración, calor en llenado…</small>
                      </span>
                      <IonIcon icon={chevronForwardOutline} className="chevron" />
                    </button>
                )}
              </IonCardContent>
            </IonCard>
            )}

            {status?.rain && (
              <IonCard>
                <IonCardContent>
                  <p className="lot-section-title">Lluvia sobre el lote</p>
                  <p className="lot-detail-stage">
                    {round1(status.rain.total)} <small>mm promedio desde el {lot.cycleStart && shortDate(lot.cycleStart)}</small>
                  </p>
                  <ul className="lot-rain-stations">
                    {status.rain.perStation.map((s) => (
                      <li key={s.id}>
                        <span>{s.name}</span>
                        <span className="station-agro-month-bar">
                          <span style={{ width: `${(s.mm / maxStationMm) * 100}%` }} />
                        </span>
                        <strong>{round1(s.mm)} mm</strong>
                      </li>
                    ))}
                  </ul>
                </IonCardContent>
              </IonCard>
            )}

            <IonCard>
              <IonCardContent>
                <p className="lot-section-title">Estaciones del lote</p>
                {lot.stations.length === 0 ? (
                  <p className="lot-detail-note">Ninguna todavía.</p>
                ) : (
                  <ul className="lot-station-list">
                    {lot.stations.map((s) => (
                      <li key={s.id}>
                        <button disabled={admin} onClick={() => history.push(`/app/devices/${s.id}`)}>
                          {s.name}
                          <small>{[s.hasAir ? 'clima' : null, s.hasRain ? 'pluviómetro' : null].filter(Boolean).join(' · ') || 'sin clima ni lluvia'}</small>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </IonCardContent>
            </IonCard>

            {!admin && isOwner && <LotShareCard lotId={lot.id} />}

            {!admin && isOwner && (
              <DeleteRow
                title="Eliminar lote"
                hint="Las estaciones no se borran: quedan sin lote"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              />
            )}
          </div>
        )}

        <IonAlert
          isOpen={observing !== null}
          onDidDismiss={() => setObserving(null)}
          header="Corregir con el campo"
          message={
            lot && observing !== null
              ? `¿El lote está hoy en ${stageTitle(lot.stages[observing])}? El acumulado pasa a ${formatGdd(lot.stages[observing].gdd)} °Cd.`
              : ''
          }
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            {
              text: 'Corregir',
              handler: () => {
                if (observing !== null) observeStage(observing);
              },
            },
          ]}
        />
        <IonAlert
          isOpen={confirmRestart}
          onDidDismiss={() => setConfirmRestart(false)}
          header="¿Reiniciar el ciclo?"
          message="Los grados día vuelven a contar desde hoy (corte o pastoreo)."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            {
              text: 'Reiniciar',
              handler: () => {
                if (status) restartCycle(status.today);
              },
            },
          ]}
        />
        <IonAlert
          isOpen={confirmDelete}
          onDidDismiss={() => setConfirmDelete(false)}
          header="¿Eliminar este lote?"
          message="Las estaciones no se borran: quedan sin lote."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            {
              text: 'Eliminar',
              role: 'destructive',
              handler: () => {
                remove().then((ok) => ok && history.replace('/app/lots'));
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default LotDetail;
